// src/components/forms/variants/v2/base/useEmailForm.ts

'use client'

import { clientLogger, logClientError } from '~/lib/logger-client'
import { useState, useCallback, useEffect, useRef } from 'react'
import { z } from 'zod'
import type { 
  UseEmailFormReturn,
  ValidationConfig,
  CRMConfig,
  EmailTemplateConfig,
  AnalyticsConfig,
  ValidTemplateAlias,
} from '~/components/forms/base/types'
import { VALID_TEMPLATE_ALIASES } from '~/components/forms/base/types'
import {
  flattenTemplateModel,
  toRecord,
  type TemplateModelStructure,
} from '~/components/forms/base/flatten-template-model'
import { sendEmail } from '~/server/actions/send-email'
import { addToCRM } from '~/server/actions/crm-integration'
import { EventTypes, sendEvent } from '~/lib/gtag'
import { PERSONAL_EMAIL_DOMAINS, isLikelyLeadSpamEmail } from '~/lib/validation/email-domains'

/**
 * Creates a Zod schema for email validation based on provided configuration.
 *
 * @param {ValidationConfig} [validation] - Optional validation configuration
 * @returns {z.ZodType<string, string>} A configured Zod schema for email validation
 */
const createEmailSchema = (validation?: ValidationConfig): z.ZodType<string, string> => {
  const emptyMessage = validation?.messages?.required ?? 'Email address is required'
  
  const baseSchema = z.string()
    .trim()
    .min(1, { error: emptyMessage })
    .check(z.email({ error: validation?.messages?.invalid ?? 'Please enter a valid email address' }))
    .transform(email => email.toLowerCase())
    .check((ctx) => {
      if (ctx.value.endsWith('.con')) {
        ctx.issues.push({
          code: 'custom',
          message: 'Did you mean .com?',
          input: ctx.value
        })
      }
    })
    .check((ctx) => {
      const domain = ctx.value.split('@')[1]
      if (!domain || domain.length < 2 || !domain.includes('.')) {
        ctx.issues.push({
          code: 'custom',
          message: 'Invalid email domain',
          input: ctx.value
        })
      }
    })
    .check((ctx) => {
      if (ctx.value.length > 254) {
        ctx.issues.push({
          code: 'custom',
          message: 'Email address is too long',
          input: ctx.value
        })
      }
    })
    .check((ctx) => {
      const localPart = ctx.value.split('@')[0]
      if (!localPart || localPart.length < 1 || /^[0-9]+$/.test(localPart)) {
        ctx.issues.push({
          code: 'custom',
          message: 'Email cannot contain only numbers',
          input: ctx.value
        })
      }
    })
    .check((ctx) => {
      const domain = ctx.value.split('@')[1]
      if (domain && (domain.startsWith('temp.') || domain.startsWith('disposable.'))) {
        ctx.issues.push({
          code: 'custom',
          message: 'Temporary email addresses are not allowed',
          input: ctx.value
        })
      }
    })
    // Reject placeholder / disposable / generated-looking lead addresses — the
    // same lead-ingress classifier guards the request-access wizard and CRM
    // upsert. (Transactional/auth sends via `sendEmail` use the deterministic
    // exact blocklist, not this heuristic.) These addresses produce bounces and
    // junk contacts.
    .check((ctx) => {
      if (isLikelyLeadSpamEmail(ctx.value)) {
        ctx.issues.push({
          code: 'custom',
          message: validation?.messages?.invalid ?? 'Please enter a valid email address',
          input: ctx.value
        })
      }
    })

  if (!validation?.rules) {
    return baseSchema
  }

  const { rules } = validation

  let schema: z.ZodType<string, string> = baseSchema

  if (rules.blockedDomains && rules.blockedDomains.length > 0) {
    schema = schema.check((ctx) => {
      if (rules.blockedDomains?.some(domain => 
        ctx.value.endsWith(`@${domain.toLowerCase()}`))) {
        ctx.issues.push({
          code: 'custom',
          message: validation.messages?.blocked ?? 'This email domain is not allowed',
          input: ctx.value
        })
      }
    })
  }

  if (rules.allowedDomains && rules.allowedDomains.length > 0) {
    schema = schema.check((ctx) => {
      if (!rules.allowedDomains?.some(domain => 
        ctx.value.endsWith(`@${domain.toLowerCase()}`))) {
        ctx.issues.push({
          code: 'custom',
          message: 'Please use an allowed email domain',
          input: ctx.value
        })
      }
    })
  }

  if (rules.requireCorporateEmail) {
    schema = schema.check((ctx) => {
      if ([...PERSONAL_EMAIL_DOMAINS].some(domain =>
        ctx.value.endsWith(`@${domain}`))) {
        ctx.issues.push({
          code: 'custom',
          message: validation.messages?.corporate ?? 'Please use your corporate email address',
          input: ctx.value
        })
      }
    })
  }

  if (rules.customRules && rules.customRules.length > 0) {
    for (const rule of rules.customRules) {
      schema = schema.check(async (ctx) => {
        try {
          const result = await Promise.resolve(rule.test(ctx.value))
          if (!result) {
            ctx.issues.push({
              code: 'custom',
              message: rule.message,
              input: ctx.value
            })
          }
        } catch {
          ctx.issues.push({
            code: 'custom',
            message: rule.message,
            input: ctx.value
          })
        }
      })
    }
  }

  return schema
}

/**
 * Configuration options for the useEmailForm hook.
 *
 * @interface UseEmailFormProps
 * @property {ValidationConfig} [validation] - Validation configuration for email input
 * @property {CRMConfig} [crm] - CRM integration configuration
 * @property {EmailTemplateConfig} [template] - Email template configuration
 * @property {AnalyticsConfig} [analytics] - Analytics tracking configuration
 * @property {Function} [onSubmit] - Custom submission handler
 * @property {Function} [onError] - Error handler callback
 * @property {Function} [onSuccess] - Success handler callback
 * @property {Function} [onChange] - Change handler callback
 * @property {Function} [onValidation] - Validation state change handler
 */
interface UseEmailFormProps {
  /** Validation configuration for email input */
  validation?: ValidationConfig
  /** CRM integration configuration */
  crm?: CRMConfig
  /** Email template configuration */
  template?: EmailTemplateConfig
  /** Analytics tracking configuration */
  analytics?: AnalyticsConfig
  /** Custom submission handler */
  onSubmit?: (email: string) => Promise<void> | void
  /** Error handler callback */
  onError?: (error: Error) => void
  /** Success handler callback */
  onSuccess?: () => void
  /** Change handler callback */
  onChange?: (email: string) => void
  /** Validation state change handler */
  onValidation?: (isValid: boolean) => void
}

/**
 * Hook for managing email form state, validation, and submission.
 * 
 * Handles:
 * - Email validation with configurable rules
 * - Form submission state
 * - CRM integration
 * - Email template sending
 * - Analytics tracking
 * - Error handling
 * 
 * @param {UseEmailFormProps} props - Configuration options for the email form
 * @returns {UseEmailFormReturn} Form state and handlers
 * @example
 * // Basic usage
 * const { email, handleChange, handleSubmit, error, isSubmitting } = useEmailForm()
 * 
 * // With validation and CRM
 * const form = useEmailForm({
 *   validation: {
 *     messages: { required: 'Email is required' },
 *     rules: { requireCorporateEmail: true }
 *   },
 *   crm: { tags: ['newsletter'] }
 * })
 */
export function useEmailForm({
  validation,
  crm,
  template,
  analytics,
  onSubmit,
  onError,
  onSuccess,
  onChange,
  onValidation
}: UseEmailFormProps = {}): UseEmailFormReturn {
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [isValid, setIsValid] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [isTouched, setIsTouched] = useState(false)

  const validateTimeoutRef = useRef<NodeJS.Timeout>(undefined)
  const emailSchema = useRef<z.ZodType<string, string>>(createEmailSchema(validation))

  // Update schema when validation config changes
  useEffect(() => {
    emailSchema.current = createEmailSchema(validation)
  }, [validation])

  /**
   * Validates the current email value against the schema.
   *
   * @returns {Promise<boolean>} Promise resolving to boolean indicating validity
   */
  const validate = useCallback(async (): Promise<boolean> => {
    if (!email) {
      setIsValid(false)
      setError(validation?.messages?.required ?? 'Please enter your email address')
      onValidation?.(false)
      return false
    }

    try {
      await emailSchema.current.parseAsync(email)
      setIsValid(true)
      setError(null)
      onValidation?.(true)
      return true
    } catch (err) {
      const errorMessage = err instanceof z.ZodError ? err.issues[0]?.message : 'Invalid email'
      setIsValid(false)
      setError(errorMessage ?? 'Invalid email')
      onValidation?.(false)
      return false
    }
  }, [email, validation, onValidation])

  /**
   * Handles input change events.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - Change event from input element
   */
  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- React Compiler is not enabled in this app (no reactCompiler in next.config.ts), so this 'compilation skipped' hint has no runtime effect; the manual memoization is correct as written
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim()
    setEmail(value)
    setError(null)
    setIsDirty(true)
    onChange?.(value)

    // Debounced validation
    if (validation?.timing?.validateOnChange) {
      if (validateTimeoutRef.current) {
        clearTimeout(validateTimeoutRef.current)
      }
      validateTimeoutRef.current = setTimeout(() => {
        void validate()
      }, validation.timing.debounceMs ?? 300)
    }
  }, [onChange, validate, validation?.timing])

  /**
   * Handles input blur events.
   */
  const handleBlur = useCallback(() => {
    setIsTouched(true)
    if (validation?.timing?.validateOnBlur) {
      void validate()
    }
  }, [validate, validation?.timing])

  /**
   * Handles form submission.
   *
   * @param {React.FormEvent} e - Form submit event
   * @throws {Error} Throws if email sending or CRM integration fails
   */
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Always validate on submit
    const isValid = await validate()
    if (!isValid) return

    if (!email || isSubmitting || isSuccess) return

    // Track submission attempt
    if (analytics?.enabled) {
      sendEvent({
        action: EventTypes.SIGNUP_ATTEMPT,
        category: analytics.category ?? 'form',
        label: analytics.label ?? 'email_signup',
        value: 1,
        ...analytics.customDimensions
      })
    }

    try {
      setIsSubmitting(true)
      setError(null)

      // Handle CRM integration - enabled by default unless explicitly disabled
      const shouldAddToCRM = crm?.options?.addToList !== false
      if (shouldAddToCRM) {
        const crmConfig = crm ?? { tags: ['email_capture'] }
        const retryAttempts = crmConfig.options?.retryAttempts ?? 1
        const retryDelay = crmConfig.options?.retryDelay ?? 1000

        const attemptCRMOperation = async (attempt: number): Promise<void> => {
          try {
            // Prefer the explicitly configured `source` for signup attribution;
            // fall back to the first tag (legacy behaviour) and finally a
            // generic default. Forms that set `crm.source` (e.g. every
            // calculator) previously had it silently dropped in favour of
            // tags[0], mis-attributing the lead's signup_source. `customFields`
            // (e.g. calculator inputs/results) are forwarded too — addToCRM
            // degrades gracefully if a field isn't provisioned in HubSpot yet.
            await addToCRM(
              email,
              crmConfig.source ?? crmConfig.tags?.[0] ?? 'email_capture',
              crmConfig.customFields,
            )
          } catch (error) {
            clientLogger.warn(`CRM attempt ${attempt} failed`, { component: 'useEmailForm', cause: error instanceof Error ? error.message : String(error) })
            if (attempt < retryAttempts && crmConfig.options?.retryOnFailure) {
              await new Promise(resolve => setTimeout(resolve, retryDelay))
              return attemptCRMOperation(attempt + 1)
            }
            // Don't throw CRM errors, just log them
            logClientError(error, { component: 'useEmailForm', context: 'Failed to add to CRM after retries' })
          }
        }

        await attemptCRMOperation(1)
      }

      // Send email if template is provided
      if (template) {
        // Validate template alias
        if (!VALID_TEMPLATE_ALIASES.includes(template.templateAlias as ValidTemplateAlias)) {
          throw new Error(`Invalid template alias: ${template.templateAlias}. Must be one of: ${VALID_TEMPLATE_ALIASES.join(', ')}`)
        }

        // Convert template models to plain objects
        const baseModel = {
          ...toRecord(template.templateModel.base),
          name: email.split('@')[0], // Add user data
          email
        }

        // Prepare the template model structure based on template type
        const templateModelStructure: TemplateModelStructure = {
          base: baseModel,
          custom: template.templateModel.custom ? {
            ...toRecord(template.templateModel.custom),
            timestamp: new Date().toISOString()
          } : undefined
        }

        // Add specific template models based on which ones are provided
        if (template.templateModel.gatedResource) {
          templateModelStructure.gatedResource = toRecord(template.templateModel.gatedResource)
        }
        
        if (template.templateModel.gatedContentNoDownload) {
          templateModelStructure.gatedContentNoDownload = toRecord(template.templateModel.gatedContentNoDownload)
        }
        
        if (template.templateModel.resourceCenterToolResults) {
          templateModelStructure.resourceCenterToolResults = toRecord(template.templateModel.resourceCenterToolResults)
        }
        
        if (template.templateModel.newsletter) {
          templateModelStructure.newsletter = toRecord(template.templateModel.newsletter)
        }
        
        if (template.templateModel.earlyAccessApplication) {
          templateModelStructure.earlyAccessApplication = toRecord(template.templateModel.earlyAccessApplication)
        }

        // Prepare email payload with flattened template model. Resource-
        // download emails (gatedResource) are landing-page / lead-gen sends:
        //  - serverPreference 'landing': send from (and resolve the template
        //    on) the landing-page Postmark server — these are NOT storefront
        //    emails. The gated-resource-1 template must be synced to that
        //    server.
        //  - trackLinks 'None': the tracking redirect (track.pstmrk.it) is
        //    blocked by ad/tracker blockers, which would block the download
        //    link itself (PR #190).
        //  - broadcast stream: this is a marketing email and the base layout
        //    renders Postmark's one-click unsubscribe, which belongs on a
        //    broadcast (not transactional) stream.
        const isResourceDownload = Boolean(template.templateModel.gatedResource)
        const emailPayload = {
          to: email,
          from: template.from ?? process.env.NEXT_PUBLIC_EMAIL_FROM ?? 'info@adsights.ai',
          templateAlias: template.templateAlias as ValidTemplateAlias,
          templateModel: flattenTemplateModel(templateModelStructure),
          ...(isResourceDownload
            ? { trackLinks: 'None' as const, serverPreference: 'landing' as const, messageStream: 'broadcast' }
            : {}),
        }

        await sendEmail(emailPayload)
      }

      // Custom submit handler
      if (onSubmit) {
        await Promise.resolve(onSubmit(email))
      }

      setIsSuccess(true)
      onSuccess?.()

      // Track successful submission
      if (analytics?.enabled) {
        sendEvent({
          action: EventTypes.SIGNUP_SUCCESS,
          category: analytics.category ?? 'engagement',
          label: analytics.label ?? 'email_signup',
          page_location: analytics.pageLocation ?? 'email_capture_form',
          element_id: analytics.elementId ?? 'email_capture_button',
          value: 1,
          ...analytics.customDimensions
        })
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong'
      setError(message)
      setIsSuccess(false) // Ensure success state is false on error
      onError?.(err as Error)

      // Track error
      if (analytics?.enabled) {
        sendEvent({
          action: 'signup_error',
          category: analytics.category ?? 'error',
          label: message,
          value: 0,
          ...analytics.customDimensions
        })
      }
      throw err // Re-throw to ensure error state is properly handled
    } finally {
      setIsSubmitting(false)
    }
  }, [email, isSubmitting, isSuccess, validate, crm, template, onSubmit, onSuccess, onError, analytics])

  /**
   * Resets the form state to initial values.
   */
  const handleReset = useCallback(() => {
    setEmail('')
    setError(null)
    setIsSubmitting(false)
    setIsSuccess(false)
    setIsValid(false)
    setIsDirty(false)
    setIsTouched(false)
  }, [])

  // Cleanup
  useEffect(() => {
    return () => {
      if (validateTimeoutRef.current) {
        clearTimeout(validateTimeoutRef.current)
      }
    }
  }, [])

  return {
    email,
    error,
    isSubmitting,
    isSuccess,
    isValid,
    isDirty,
    isTouched,
    handleChange,
    handleSubmit,
    handleBlur,
    handleReset,
    validate
  }
}
